import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import { useDispatch } from "react-redux";
import Axios from "axios";
import {
    Drawer,
    Select,
    Button,
    Typography,
    Tag,
    notification,
    Switch,
    Spin,
} from "antd";
import {
    KeyOutlined,
    DisconnectOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    ArrowLeftOutlined,
    SyncOutlined,
    ReloadOutlined,
    CloudDownloadOutlined,
    ArrowUpOutlined,
} from "@ant-design/icons";
import { updateSettings } from "store/Actions/settings";
import { Tokens } from "Shared/Tokens";
import { ThemeSwitcher } from "Shared/ThemeSwitcher";
import { APP_VERSION, checkAppVersion, applyAppUpdate } from "utils/appVersion";

const { Text } = Typography;

const cardStyle = {
    backgroundColor: Tokens.card,
    border: `1px solid ${Tokens.hairline}`,
    borderRadius: Tokens.radiusCard,
    padding: "16px",
    marginBottom: "14px",
};

const sectionHeaderStyle = {
    fontSize: "13px",
    fontWeight: 500,
    color: Tokens.textSecondary,
    marginBottom: "8px",
    display: "block",
};

const rowStyle = (isLast = false) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "10px 0",
    borderBottom: isLast ? "none" : `1px solid ${Tokens.hairline}`,
});

const compactBtnStyle = {
    height: "34px",
    borderRadius: Tokens.radiusControl,
    fontSize: "13px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};

const BinancePreferences = (props) => {
    const {
        onClose = () => {},
        open = false,
        visible = false,
        sortBy,
        setSortBy,
        currency,
        setCurrency,
        timeRange,
        setTimeRange,
        cutoffDate = "2026-01-01",
        setCutoffDate,
        supportedCurrencies = [],
        hasConnectedBinance = true,
        hideSmallBalances = false,
        setHideSmallBalances,
        onOpenUpdateDrawer,
        onDisconnectSuccess,
    } = props;

    const isOpen = Boolean(open || visible);
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );
    const [disconnecting, setDisconnecting] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [syncingLedger, setSyncingLedger] = useState(false);
    const [checkingVersion, setCheckingVersion] = useState(false);
    const [versionStatus, setVersionStatus] = useState("idle");
    const [remoteVersion, setRemoteVersion] = useState(APP_VERSION);
    const [apiVersion, setApiVersion] = useState(null);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const runVersionCheck = useCallback(async (showNotification = false) => {
        setCheckingVersion(true);
        try {
            const result = await checkAppVersion();
            setRemoteVersion(result.remoteVersion || APP_VERSION);
            if (result.apiVersion) {
                setApiVersion(result.apiVersion);
            }
            if (result.updateAvailable) {
                setVersionStatus("update-available");
                if (showNotification) {
                    notification.info({
                        message: "Update available",
                        description: `v${result.remoteVersion} is ready to install.`,
                    });
                }
            } else {
                setVersionStatus("up-to-date");
                if (showNotification) {
                    notification.success({
                        message: "Already up to date",
                        description: `Running v${APP_VERSION}`,
                    });
                }
            }
        } catch (err) {
            setVersionStatus("error");
            if (showNotification) {
                notification.error({
                    message: "Couldn't check for updates",
                    description: err.message || "Unable to reach update server",
                });
            }
        } finally {
            setCheckingVersion(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            runVersionCheck(false);
        }
    }, [isOpen, runVersionCheck]);

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            const apiBase =
                process.env.REACT_APP_API || "https://api.rajchandra.me";
            const api = `${apiBase}/binance/auth/connect`;
            await Axios.delete(api, { withCredentials: true });
            notification.success({
                message: "Binance disconnected",
                description: "Your Binance API credentials have been removed.",
            });
            if (onDisconnectSuccess) {
                onDisconnectSuccess();
            }
            onClose();
        } catch (error) {
            notification.error({
                message: "Disconnect failed",
                description:
                    "Could not remove Binance credentials. Please try again.",
            });
        } finally {
            setDisconnecting(false);
        }
    };

    const handleResyncLedger = async () => {
        setSyncingLedger(true);
        try {
            const apiBase =
                process.env.REACT_APP_API || "https://api.rajchandra.me";
            await Axios.post(
                `${apiBase}/binance/report-card/sync`,
                {},
                { withCredentials: true }
            );
            notification.success({
                message: "Trade ledger sync started",
                description:
                    "Your Binance trade history is being synchronized.",
            });
        } catch (error) {
            notification.error({
                message: "Sync failed",
                description: "Could not trigger trade ledger synchronization.",
            });
        } finally {
            setSyncingLedger(false);
        }
    };

    const handleApplyUpdate = async () => {
        notification.info({
            message: "Updating application",
            description: `Applying v${
                remoteVersion || APP_VERSION
            } and refreshing cache...`,
        });
        await applyAppUpdate();
    };

    const sortOptions = [
        { label: "Current value", value: "currentValue" },
        { label: "Invested amount", value: "investment" },
        { label: "ROI %", value: "roi" },
        { label: "Price", value: "price" },
        { label: "Quantity", value: "quantity" },
        { label: "Name", value: "name" },
    ];

    const currencyOptions =
        supportedCurrencies.length > 0
            ? supportedCurrencies.map((curr) => ({
                  label: curr.toUpperCase(),
                  value: curr.toUpperCase(),
              }))
            : [
                  { label: "USDT", value: "USDT" },
                  { label: "USD", value: "USD" },
                  { label: "INR", value: "INR" },
                  { label: "EUR", value: "EUR" },
                  { label: "GBP", value: "GBP" },
                  { label: "BTC", value: "BTC" },
                  { label: "ETH", value: "ETH" },
                  { label: "BNB", value: "BNB" },
              ];

    const timeRangeOptions = [
        { label: "1d", value: 1 },
        { label: "7d", value: 7 },
        { label: "30d", value: 30 },
        { label: "90d", value: 90 },
        { label: "180d", value: 180 },
        { label: "1y", value: 365 },
        { label: "All time", value: "all" },
    ];

    const cutoffPresetOptions = [
        { label: "Since Jan 2026", value: "2026-01-01" },
        { label: "Cycle · Aug 24, 2026", value: "2026-08-24" },
        { label: "Since Oct 2026", value: "2026-10-01" },
        { label: "Since Jan 2025", value: "2025-01-01" },
        { label: "All time", value: "all" },
    ];
    const cutoffOptions = cutoffPresetOptions.some(
        (o) => o.value === (cutoffDate || "2026-01-01")
    )
        ? cutoffPresetOptions
        : [
              {
                  label: `Since ${cutoffDate}`,
                  value: cutoffDate,
              },
              ...cutoffPresetOptions,
          ];

    const updateUrlParam = (key, val) => {
        if (!location || !history || typeof history.replace !== "function") {
            return;
        }
        const params = new URLSearchParams(location.search || "");
        params.set(key, val);
        history.replace({
            pathname: location.pathname,
            search: params.toString(),
        });
    };

    return (
        <Drawer
            title={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <Button
                        type="text"
                        icon={
                            <ArrowLeftOutlined
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "16px",
                                }}
                            />
                        }
                        onClick={onClose}
                        title="Close settings"
                        style={{
                            padding: 0,
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    />
                    <Text
                        style={{
                            fontSize: "15px",
                            color: Tokens.textPrimary,
                            fontWeight: 600,
                        }}
                    >
                        Binance settings &amp; API keys
                    </Text>
                </div>
            }
            placement={isMobile ? "bottom" : "right"}
            push={false}
            width={isMobile ? "100%" : 460}
            height={isMobile ? "85%" : "100%"}
            onClose={onClose}
            open={isOpen}
            headerStyle={{
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
                padding: "14px 16px",
            }}
            bodyStyle={{
                backgroundColor: Tokens.bg,
                color: Tokens.textPrimary,
                padding: "16px",
            }}
        >
            <div
                style={{
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                    width: "100%",
                }}
            >
                {/* 1. Display Preferences Card */}
                <div data-testid="binance-preferences-card" style={cardStyle}>
                    <span style={sectionHeaderStyle}>Display preferences</span>

                    <div style={rowStyle(false)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Theme
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Light default, Dark, or Auto system preference
                            </div>
                        </div>
                        <ThemeSwitcher testIdPrefix="binance-settings" />
                    </div>

                    <div style={rowStyle(false)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Sort order
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Order exchange holdings by metric
                            </div>
                        </div>
                        <Select
                            value={sortBy}
                            style={{ width: 165 }}
                            onChange={(value) => {
                                if (setSortBy) {
                                    setSortBy(value);
                                }
                                dispatch(
                                    updateSettings({
                                        binanceSortBy: value,
                                        defaultSortBy: value,
                                    })
                                );
                                updateUrlParam("sortBy", value);
                            }}
                            options={sortOptions}
                        />
                    </div>

                    <div style={rowStyle(false)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Base currency
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Quote currency for valuation and P&amp;L
                            </div>
                        </div>
                        <Select
                            value={currency}
                            style={{ width: 130 }}
                            onChange={(value) => {
                                if (setCurrency) {
                                    setCurrency(value);
                                }
                                dispatch(
                                    updateSettings({
                                        binanceCurrency: value,
                                        currency: value,
                                    })
                                );
                                updateUrlParam("currency", value);
                            }}
                            options={currencyOptions}
                        />
                    </div>

                    <div style={rowStyle(false)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Time horizon
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Historical window for portfolio analytics
                            </div>
                        </div>
                        <Select
                            value={timeRange}
                            style={{ width: 130 }}
                            onChange={(value) => {
                                if (setTimeRange) {
                                    setTimeRange(value);
                                }
                                dispatch(
                                    updateSettings({
                                        binanceTimeRange: value,
                                    })
                                );
                                updateUrlParam(
                                    "days",
                                    value === "all" ? "all" : String(value)
                                );
                            }}
                            options={timeRangeOptions}
                        />
                    </div>

                    <div style={rowStyle(false)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Cutoff date (start fresh)
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Compute cost basis and P&amp;L from this date
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                                alignItems: "flex-end",
                            }}
                        >
                            <Select
                                data-testid="binance-cutoff-select"
                                value={cutoffDate || "2026-01-01"}
                                style={{ width: 150 }}
                                onChange={(value) => {
                                    if (setCutoffDate) {
                                        setCutoffDate(value);
                                    }
                                    dispatch(
                                        updateSettings({
                                            binanceCutoffDate: value,
                                        })
                                    );
                                    updateUrlParam("cutoffDate", value);
                                }}
                                options={cutoffOptions}
                            />
                            <input
                                type="date"
                                aria-label="Custom cutoff date"
                                data-testid="binance-cutoff-date-input"
                                value={
                                    cutoffDate && cutoffDate !== "all"
                                        ? cutoffDate
                                        : ""
                                }
                                onChange={(e) => {
                                    const val = e.target.value || "all";
                                    if (setCutoffDate) {
                                        setCutoffDate(val);
                                    }
                                    dispatch(
                                        updateSettings({
                                            binanceCutoffDate: val,
                                        })
                                    );
                                    updateUrlParam("cutoffDate", val);
                                }}
                                style={{
                                    width: 150,
                                    height: "30px",
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusControl,
                                    padding: "0 8px",
                                    fontSize: "12px",
                                    fontFamily: Tokens.fontMono,
                                }}
                            />
                        </div>
                    </div>

                    <div style={rowStyle(true)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Hide small balances (&lt; $1.00)
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Filter out dust positions from holdings list
                            </div>
                        </div>
                        <Switch
                            checked={Boolean(hideSmallBalances)}
                            onChange={(checked) => {
                                if (setHideSmallBalances) {
                                    setHideSmallBalances(checked);
                                }
                                dispatch(
                                    updateSettings({
                                        hideSmallBalances: checked,
                                    })
                                );
                            }}
                        />
                    </div>
                </div>

                {/* 2. API Credentials & Exchange Connection Card */}
                <div data-testid="binance-connection-card" style={cardStyle}>
                    <span style={sectionHeaderStyle}>
                        API credentials &amp; connection
                    </span>

                    <div style={rowStyle(false)}>
                        <Text
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "13px",
                            }}
                        >
                            Connection status
                        </Text>
                        {hasConnectedBinance ? (
                            <Tag
                                icon={<CheckCircleOutlined />}
                                style={{
                                    backgroundColor: Tokens.positiveBg,
                                    color: Tokens.positive,
                                    border: `1px solid ${Tokens.positiveBorder}`,
                                    borderRadius: Tokens.radiusTag,
                                    padding: "2px 8px",
                                    margin: 0,
                                }}
                            >
                                Active API key
                            </Tag>
                        ) : (
                            <Tag
                                icon={<ExclamationCircleOutlined />}
                                style={{
                                    backgroundColor: Tokens.negativeBg,
                                    color: Tokens.negative,
                                    border: `1px solid ${Tokens.negativeBorder}`,
                                    borderRadius: Tokens.radiusTag,
                                    padding: "2px 8px",
                                    margin: 0,
                                }}
                            >
                                Not connected
                            </Tag>
                        )}
                    </div>

                    {hasConnectedBinance && (
                        <div style={rowStyle(false)}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    Re-sync trade ledger
                                </div>
                                <div
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "12px",
                                    }}
                                >
                                    Refresh historical trades and cost basis
                                </div>
                            </div>
                            <Button
                                data-testid="binance-resync-ledger-btn"
                                type="default"
                                icon={<SyncOutlined spin={syncingLedger} />}
                                loading={syncingLedger}
                                onClick={handleResyncLedger}
                                style={{
                                    ...compactBtnStyle,
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairline}`,
                                }}
                            >
                                Re-sync
                            </Button>
                        </div>
                    )}

                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            paddingTop: "12px",
                        }}
                    >
                        <Button
                            type="default"
                            icon={
                                <KeyOutlined
                                    style={{ color: Tokens.textSecondary }}
                                />
                            }
                            onClick={() => {
                                onClose();
                                if (onOpenUpdateDrawer) {
                                    onOpenUpdateDrawer();
                                }
                            }}
                            style={{
                                ...compactBtnStyle,
                                flex: 1,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textPrimary,
                                border: `1px solid ${Tokens.hairline}`,
                            }}
                        >
                            {hasConnectedBinance
                                ? "Update API keys"
                                : "Connect Binance account"}
                        </Button>

                        {hasConnectedBinance &&
                            (confirmDisconnect ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <Button
                                        danger
                                        type="primary"
                                        data-testid="confirm-disconnect-binance-btn"
                                        loading={disconnecting}
                                        onClick={handleDisconnect}
                                        style={compactBtnStyle}
                                    >
                                        Confirm
                                    </Button>
                                    <Button
                                        data-testid="cancel-disconnect-binance-btn"
                                        onClick={() =>
                                            setConfirmDisconnect(false)
                                        }
                                        style={{
                                            ...compactBtnStyle,
                                            backgroundColor: Tokens.raised,
                                            color: Tokens.textSecondary,
                                            border: `1px solid ${Tokens.hairline}`,
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    danger
                                    data-testid="disconnect-binance-btn"
                                    icon={<DisconnectOutlined />}
                                    loading={disconnecting}
                                    onClick={() => setConfirmDisconnect(true)}
                                    style={compactBtnStyle}
                                >
                                    Disconnect
                                </Button>
                            ))}
                    </div>
                </div>

                {/* 3. Application Updates & Build Telemetry Card */}
                <div
                    data-testid="binance-system-card"
                    style={{ ...cardStyle, marginBottom: 0 }}
                >
                    <span style={sectionHeaderStyle}>
                        Application &amp; system
                    </span>

                    <div style={rowStyle(true)}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Application updates
                            </div>
                            <div
                                style={{
                                    fontSize: "12px",
                                    marginTop: "2px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {checkingVersion && (
                                    <span
                                        style={{ color: Tokens.textSecondary }}
                                    >
                                        <Spin size="small" /> Checking for
                                        updates...
                                    </span>
                                )}
                                {!checkingVersion &&
                                    versionStatus === "update-available" && (
                                        <span
                                            style={{
                                                color: Tokens.accent,
                                                fontWeight: 500,
                                            }}
                                        >
                                            <ArrowUpOutlined /> v{remoteVersion}{" "}
                                            available (current: v{APP_VERSION})
                                        </span>
                                    )}
                                {!checkingVersion &&
                                    versionStatus === "up-to-date" && (
                                        <span
                                            style={{ color: Tokens.positive }}
                                        >
                                            <CheckCircleOutlined /> Up to date
                                            (v{APP_VERSION})
                                            {apiVersion
                                                ? ` · API v${apiVersion}`
                                                : ""}
                                        </span>
                                    )}
                                {!checkingVersion &&
                                    (versionStatus === "idle" ||
                                        versionStatus === "error") && (
                                        <span
                                            style={{
                                                color: Tokens.textSecondary,
                                            }}
                                        >
                                            Frontend: v{APP_VERSION}
                                            {apiVersion
                                                ? ` · API: v${apiVersion}`
                                                : ""}
                                        </span>
                                    )}
                            </div>
                        </div>

                        {versionStatus === "update-available" ? (
                            <Button
                                type="primary"
                                icon={<CloudDownloadOutlined />}
                                onClick={handleApplyUpdate}
                                style={compactBtnStyle}
                            >
                                Update now
                            </Button>
                        ) : (
                            <Button
                                type="default"
                                icon={<ReloadOutlined spin={checkingVersion} />}
                                loading={checkingVersion}
                                onClick={() => runVersionCheck(true)}
                                style={{
                                    ...compactBtnStyle,
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairline}`,
                                }}
                            >
                                Check for updates
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default BinancePreferences;
